import { faPlus } from '@fortawesome/pro-solid-svg-icons'
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom'
import { useLazyGetUsersQuery } from '../../../../Redux/Api/Organisations';
import { useDeleteUserMutation } from '../../../../Redux/Api/Users';
import { useAppSelector } from '../../../../Redux/store'
import { Hydra } from '../../../../Utils/Hydra';
import BusyArea from '../../../Shared/BusyArea';
import Button from '../../../Shared/Form/Button';
import StatusPopups from '../../../Shared/Form/StatusPopups';
import { GridRenderCellParams, GridValueGetterParams } from '@mui/x-data-grid';
import Title from '../../../Shared/Title';
import LoadingPage from '../../LoadingPage';
import { useTranslation } from "react-i18next";
import { useMediaQuery } from "@mui/material";
import { JWT } from '../../../../Utils/JWT';
import GenericDatagrid from '../../../Datagrid/GenericDatagrid';
import { faEdit, faQrcode, faQuestion, faTrashAlt, faUsers, faUserTie } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import RowActions, { RowAction } from '../../../Datagrid/DatagridLayout/Rows/RowActions';
import StatusRenderer, { getStatusDetails } from '../../../Datagrid/Renderers/StatusRenderer';
import moment from 'moment';
import { UserResponse } from '../../../../Redux/Api/Request/User';

const UsersPage = () => {
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);
    const [successMessage, setSuccessMessage] = useState<string>('');
    const [deleteUser] = useDeleteUserMutation();
    const navigate = useNavigate();
    const org_id = useAppSelector((s) => s.organisation.id)!;
    const [getUsers, { data: users }] = useLazyGetUsersQuery();
    const { t, i18n } = useTranslation();
    const user_id = JWT.read<string>('sub');

    const [successAddMessage, setSuccessAddMessage] = useState('');

    const isMediumScreen = useMediaQuery('(min-width: 800px)');
    const isSmallScreen = useMediaQuery('(min-width: 650px)');

    useEffect(() => {
        if (sessionStorage.getItem('successMessage')) {
            setSuccessAddMessage(sessionStorage.getItem('successMessage')!);
            sessionStorage.removeItem('successMessage');
        }
    }, [org_id])

    useEffect(() => {
        if (org_id) {
            getUsers(org_id);
        }
    }, [org_id, getUsers]);

    const deleteUserAction = async (id: string) => {
        if (busy) return;

        setBusy(true);
        setError('');
        setSuccessMessage('');

        try {
            await deleteUser(id);
            await getUsers(org_id);
            setSuccessMessage(t('user:list:userDeleted') || '');
        } catch (e) {
            let err = e as Hydra.Error;
            setError(err['hydra:description']);
        }

        setBusy(false);
    };

    const roleMap: Record<string, { textKey: string; icon: JSX.Element }> = {
        ROLE_ADMIN: { textKey: 'user:list:adminRole', icon: <FontAwesomeIcon icon={faUserTie} /> },
        ROLE_USER: { textKey: 'user:list:userRole', icon: <FontAwesomeIcon icon={faQrcode} /> },
        ROLE_SOCIAL: { textKey: 'user:list:socialUserRole', icon: <FontAwesomeIcon icon={faUsers} /> },
    }

    const formatRole = (role: string) => {
        const roleInfo = roleMap[role];
        if (roleInfo) {
            return {
                text: t(roleInfo.textKey),
                icon: roleInfo.icon,
            };
        }
        return {
            text: t('user:list:unknownRole'),
            icon: <FontAwesomeIcon icon={faQuestion} />,
        };
    };

    const columns = [
        {
            field: 'firstname',
            headerName: t('user:list:name'),
            flex: 1,
            valueGetter: (params: GridValueGetterParams<UserResponse>) => {
                return `${params.row.firstname} ${params.row.lastname} ${params.row.id === user_id ? '(' + (t("patrick:you").toLocaleLowerCase()) + ')' : ''}`;
            },
        },
        {
            field: 'email',
            headerName: t('user:list:email'),
            flex: 1,
        },
        {
            field: 'role',
            headerName: t('user:list:role'),
            flex: 1,
            valueGetter: (params: GridValueGetterParams<UserResponse>) => {
                const { text } = formatRole(params.row.role);
                return text;
            },
            renderCell: (params: GridRenderCellParams<UserResponse>) => {
                const { text, icon } = formatRole(params.row.role);
                return (
                    <div className='flex items-center gap-1 overflow-hidden'>
                        {icon}
                        <span className="whitespace-nowrap overflow-hidden text-ellipsis">
                            {text}
                        </span>
                    </div>
                );
            },
            hide: !isSmallScreen,
        },
        {
            field: 'active',
            headerName: t('user:list:status'),
            flex: 1,
            minWidth: isMediumScreen ? 120 : 50,
            maxWidth: isMediumScreen ? 120 : 50,
            valueGetter: (params: GridValueGetterParams<UserResponse>) => {
                const { text } = getStatusDetails(params.row.emailActivated ? params.row.active ? 'active' : 'locked' : 'pending');
                return text;
            },
            renderCell: (params: GridRenderCellParams<UserResponse>) => (
                <StatusRenderer status={params.row.emailActivated ? params.row.active ? 'active' : 'locked' : 'pending'} small={!isMediumScreen} />
            ),
        },
        {
            field: 'createdAt',
            headerName: t('datagrid:createdAt'),
            flex: 1,
            valueGetter: (params: GridValueGetterParams<UserResponse>) => {
                return moment(params.row.createdAt).format('DD-MM-YYYY');
            },
            hide: !isSmallScreen,
            type: 'date',
        },
        {
            field: 'actions',
            headerName: '',
            width: 120,
            renderCell: (params: GridRenderCellParams<UserResponse>) => {
                const actions: RowAction[] = [
                    {
                        label: t('cards:list:adjust'),
                        icon: <FontAwesomeIcon icon={faEdit} />,
                        onClick: () => navigate(`/${i18n.language}/dashboard/users/edit/` + params.row.id),
                    },
                    {
                        label: t('cards:list:delete'),
                        icon: <FontAwesomeIcon icon={faTrashAlt} />,
                        withModal: {
                            onYes: () => { deleteUserAction(params.row.id); }
                        }
                    }
                ];

                return <RowActions actions={actions} />;
            },
        }
    ];

    if (!users) return <LoadingPage />;

    type HydraUsersResponse = { 'hydra:member': UserResponse[] };

    const userList: UserResponse[] = (users && typeof users === 'object' && 'hydra:member' in users)
        ? (users as HydraUsersResponse)['hydra:member']
        : [];

    return (
        <>
            <div className='flex flex-wrap justify-between items-center mb-3'>
                <Title text={t('user:list:title')} textNotBold />
                <Button
                    action={() => navigate(`/${i18n.language}/dashboard/users/create`)}
                    className="my-6"
                    title={t('user:list:add')}
                    icon={faPlus}
                    iconright
                />
            </div>

            <StatusPopups setText={setError} type='error' text={error} />
            <StatusPopups setText={setSuccessMessage} type='success' text={successMessage} />
            <StatusPopups setText={setSuccessAddMessage} type='success' text={successAddMessage} />
            <BusyArea busy={busy}>
                <GenericDatagrid
                    data={userList}
                    columns={columns}
                    onRowClick={(params) => navigate(`/${i18n.language}/dashboard/users/edit/` + params.row.id)}
                    className='mb-5 lg:mb-10'
                />
            </BusyArea>
        </>
    );
};

export default UsersPage;
